import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MacrocycleProvider } from '../../providers/macrocycle-provider.services';
import { Subscription } from 'rxjs';
import {
  MAT_DIALOG_DATA,
  MatDialogActions, MatDialogClose,
  MatDialogContent, MatDialogModule,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatOption, MatOptionSelectionChange } from '@angular/material/core';
import { AnnualPlanMacroCycle } from "../../../common/models/annual-plan-macro-cycle";
import { Organization } from "../../../common/models/organization";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { LessThanNumberAnnualPlanValidator } from "../../../common/validators/less-than-number-annual-plan.validator";
import {
  GreaterThanNumberAnnualPlanValidator
} from "../../../common/validators/greater-than-number-annual-plan.validator";
import { MacroCycle } from "../../../common/models/macro-cycle";
import { TranslateModule } from "@ngx-translate/core";
import { MatError, MatFormField, MatFormFieldModule, MatLabel } from "@angular/material/form-field";
import { MatSelect, MatSelectModule } from "@angular/material/select";
import { MatButton, MatButtonModule } from "@angular/material/button";
import { NgForOf, NgIf } from "@angular/common";
import { FlexLayoutModule } from "@angular/flex-layout";


@Component({
  selector: 'annual-plan-dialog-macro-cycle',
  templateUrl: './annual-plan-dialog-form-macrocycle.component.html',
  styleUrls: ['./annual-plan-dialog-form-macrocycle.scss'],
  imports: [
    MatDialogTitle,
    TranslateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatError,
    NgForOf,
    NgIf,
    FlexLayoutModule
  ],
  standalone: true
})
export class AnnualPlanDialogFormMacrocycleComponent implements OnInit, OnDestroy {

    public macrocycleForm: FormGroup;
    public isSubmitting = false;
    public annualPlanMacroCycle: AnnualPlanMacroCycle = {} as AnnualPlanMacroCycle;
    public macroCycles: MacroCycle[] = [];

    public weeksMenuFrom: WeekMenu[] = [];
    public weeksMenuTo: WeekMenu[] = [];

    private organization: Organization;
    private subscriptions: Subscription[] = [];

    constructor(
        public dialog: MatDialogRef<AnnualPlanDialogFormMacrocycleComponent>,
        private utils: UtilsProvider,
        private fb: FormBuilder,
        private macrocycleProvider: MacrocycleProvider,
        @Inject(MAT_DIALOG_DATA) public data: any) {

        this.organization = data.organization;

        const weekMenuFrom = {};
        if (data.week) {
            Object.assign(weekMenuFrom, {number: data.week.weekNumber, realNumber: data.week.realWeekNumber});
        }

        // use the FormBuilder to create a form group
        this.macrocycleForm = this.fb.group({
            id: new FormControl(null),
            weekFrom: new FormControl(weekMenuFrom, [Validators.required]),
            weekTo: new FormControl(null, [Validators.required]),
            macrocycle: new FormControl(null, [Validators.required]),
            tag: new FormControl(null),
        }, {
            validator: [
                LessThanNumberAnnualPlanValidator('weekFrom', 'weekTo', true),
                GreaterThanNumberAnnualPlanValidator('weekTo', 'weekFrom', true),
            ]
        });

        this.disableWeeksMenuFrom(data);

        this.weeksMenuTo = data.weeks.map(function(week: any) {
            return {
                number: week.weekNumber,
                realNumber: week.realWeekNumber,
                realWeekYear: week.realWeekYear,
                isDisabled: false,
            };
        });
    }

    public ngOnInit() {
        if (this.data.annualPlanMacroCycle) {
            this.macrocycleForm.patchValue({
                id: this.data.annualPlanMacroCycle.id,
                weekFrom: {number: this.data.annualPlanMacroCycle.weekFrom},
                weekTo: {number: this.data.annualPlanMacroCycle.weekTo},
                macrocycle: this.data.annualPlanMacroCycle.macroCycle,
                tag: this.data.annualPlanMacroCycle.tag
            });
        }

        this.subscriptions.push(
            this.macrocycleProvider.findAll(this.organization).subscribe((macroCycles) => {
                this.macroCycles = macroCycles;
            })
        );
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    }

    public compareWeekMenus(o1: WeekMenu, o2: WeekMenu): boolean {
        if (null === o1 || null === o2) {
            return false;
        }

        return o1.number === o2.number;
    }

    public compareMacroCycles(o1: MacroCycle, o2: MacroCycle): boolean {
        if (null === o1 || null === o2) {
            return false;
        }

        return o1.id === o2.id;
    }

    public submitForm() {
        this.isSubmitting = true;

        if (this.macrocycleForm.valid) {
            Object.assign(this.annualPlanMacroCycle, this.macrocycleForm.value, {
                weekFrom: this.macrocycleForm.controls['weekFrom'].value.number,
                weekTo: this.macrocycleForm.controls['weekTo'].value.number,
                macroCycle: this.macroCycles.find((macrocycle) => macrocycle.id === this.macrocycleForm.controls['macrocycle'].value.id)
            });
            this.dialog.close({annualPlanMacroCycle: this.annualPlanMacroCycle, toDelete: false});
        } else {
            this.utils.validateAllFormFields(this.macrocycleForm);
            this.isSubmitting = false;
        }
    }

    get f() {
        return this.macrocycleForm.controls;
    }

    public onDelete() {
        this.dialog.close({annualPlanMacroCycle: this.data.annualPlanMacroCycle, toDelete: true});
    }

    public updateWeeksMenuTo(event: MatOptionSelectionChange, selectedNumber: any) {

        if (event.source.selected) {

            const weeksMenuTo = JSON.parse(JSON.stringify(this.weeksMenuFrom));

            let disableNextItems = false;
            for (const item of weeksMenuTo) {
                if (item.number < selectedNumber) {
                    item.isDisabled = true;

                    continue;
                }

                if (item.number >= selectedNumber && !disableNextItems) {
                    if (item.isDisabled) {
                        disableNextItems = true;
                    }
                }

                if (disableNextItems) {
                    item.isDisabled = true;
                }
            }

            this.weeksMenuTo = weeksMenuTo;
        }
    }

    private disableWeeksMenuFrom(data:any) {

        const annualPlanMacroCycles = data.annualPlanMacroCycles;
        const openedCycle = this.data.annualPlanMacroCycle;

        /**
         * Cette fonction supprime les semaines de début du cycle
         * qui ne sont pas sélectionnable. Une semaine peut être
         * sélectionnée lorsqu'elle n'est pas impactée par un cycle.
         * Autrement dit, toutes les semaines faisant partie d'un
         * cycle sont retirées de la liste.
         *
         * On a alors 2 cas de figure à traiter :
         *
         * 1) On est en train d'ajouter un nouveau cycle (openedCycle === undefined).
         *    Dans ce cas, je retire toutes les semaines impactées par un cycle, de la
         *    liste des possibilités.
         *
         * 2) On est en train de modificer un cycle existant (openedCycle !== undefined).
         *    Dans ce cas, je retire toutes les semaines impactées paur un cycle, sauf
         *    les semaines faisant partie du cycle en édition, de la liste des possibilités.
         *    On garde les semaines du cycle en édition afin de pouvoir choisir une plage
         *    plus petite par exemple : le début fixé à la semaine 15 est déplacé à la semaine
         *    16.
         */
        this.weeksMenuFrom = data.weeks.map(function(week:any) {

            // par défaut on garde la semaine
            let isDisabled = false;

            if (annualPlanMacroCycles) {
                for (const annualPlanMacroCycle of annualPlanMacroCycles) {

                    // cas 2 : édition d'un cycle
                    if (openedCycle !== undefined && annualPlanMacroCycle.tag === openedCycle.tag) {

                        // je garde cette semaine, elle fait partie du cycle en édition
                        // je ne change pas le flag
                    } else {
                        if (week.weekNumber >= annualPlanMacroCycle.weekFrom && week.weekNumber <= annualPlanMacroCycle.weekTo) {

                            // la semaine fait partie d'une phase existante
                            isDisabled = true;
                        }
                    }
                }
            }

            return {
                number: week.weekNumber,
                realNumber: week.realWeekNumber,
                realWeekYear: week.realWeekYear,
                isDisabled,
            };
        }.bind(this));
    }
}

export interface WeekMenu {
    number: number;
    realNumber: number;
    realWeekYear: number;
    isDisabled: boolean;
}
